import { useState, useEffect } from "react";

import { Trophy, Star, TrendingUp, Award } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/lib/routes";

interface GamificationData {
  total_points: number;
  current_level: number;
  points_to_next_level: number;
  sessions_created: number;
  messages_sent: number;
  memories_created: number;
  projects_created: number;
  boards_created: number;
}

interface Achievement {
  id: string;
  achievement_key: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  tier: string;
  points_reward: number;
  requirement_type: string;
  requirement_value: number;
  is_hidden: boolean;
  unlocked_at?: string;
}

const TIER_COLORS = {
  bronze: 'bg-orange-700',
  silver: 'bg-gray-400',
  gold: 'bg-yellow-500',
  platinum: 'bg-cyan-400',
  diamond: 'bg-blue-400',
};

export const GamificationDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gamData, setGamData] = useState<GamificationData | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [unlockedAchievements, setUnlockedAchievements] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchGamificationData();
  }, [user]);

  const fetchGamificationData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch gamification data
      const { data: gamificationData, error: gamError } = await supabase
        .from('user_gamification')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (gamError && gamError.code !== 'PGRST116') throw gamError;

      setGamData(gamificationData || null);

      // Fetch all achievements
      const { data: allAchievements, error: achError } = await supabase
        .from('achievements')
        .select('*')
        .order('requirement_value');

      if (achError) throw achError;

      // Fetch user's unlocked achievements
      const { data: userAchievements, error: userAchError } = await supabase
        .from('user_achievements')
        .select('achievement_id, unlocked_at')
        .eq('user_id', user.id);

      if (userAchError) throw userAchError;

      const unlockedIds = new Set(userAchievements?.map(ua => ua.achievement_id) || []);
      const unlockedMap = new Map(userAchievements?.map(ua => [ua.achievement_id, ua.unlocked_at]) || []);

      // Merge data
      const enrichedAchievements = (allAchievements || []).map(ach => ({
        ...ach,
        unlocked_at: unlockedMap.get(ach.id),
      }));

      setAchievements(enrichedAchievements);
      setUnlockedAchievements(unlockedIds);
    } catch (error) {
      console.error('Error fetching gamification data:', error);
      toast.error('Failed to load gamification data');
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!gamData) return 0;
    const pointsInCurrentLevel = gamData.total_points - getPointsForLevel(gamData.current_level);
    return (pointsInCurrentLevel / gamData.points_to_next_level) * 100;
  };

  const getPointsForLevel = (level: number): number => {
    let totalPoints = 0;
    let pointsNeeded = 100;
    for (let i = 1; i < level; i++) {
      totalPoints += pointsNeeded;
      pointsNeeded = Math.ceil(pointsNeeded * 1.1);
    }
    return totalPoints;
  };

  const getAchievementsByCategory = (category: string) => {
    return achievements.filter(a => a.category === category);
  };

  const categories = ['sessions', 'messages', 'memories', 'projects', 'boards', 'levels', 'points'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-32" speed="fast" />
          <Skeleton className="h-9 w-48" speed="fast" delay={50} />
        </div>

        {/* Level & Progress Card Skeleton */}
        <Card className="bg-linear-to-br from-primary/10 to-primary/5 animate-content-reveal">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <Skeleton className="h-7 w-24" speed="slow" />
                <Skeleton className="h-4 w-32" speed="slow" delay={75} />
              </div>
              <Skeleton className="h-8 w-8 rounded-full" speed="slow" delay={50} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <Skeleton className="h-4 w-28" speed="slow" delay={100} />
                <Skeleton className="h-4 w-20" speed="slow" delay={125} />
              </div>
              <Skeleton className="h-3 w-full" speed="slow" delay={150} />
              <Skeleton className="h-3 w-32 ml-auto" speed="slow" delay={175} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-4 border-t">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="text-center p-3 rounded-lg bg-background/50">
                  <Skeleton className="h-6 w-8 mx-auto mb-1" speed="slow" delay={i * 50} />
                  <Skeleton className="h-3 w-12 mx-auto" speed="slow" delay={i * 50 + 25} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Achievements Card Skeleton */}
        <Card className="animate-content-reveal" style={{ animationDelay: '100ms' }}>
          <CardHeader className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" delay={50} />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-10 w-full mb-4" delay={75} />
            <div className="space-y-3 mt-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
                  <Skeleton className="h-12 w-12 rounded-full" delay={i * 80} />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-32" delay={i * 80 + 30} />
                      <Skeleton className="h-5 w-16" delay={i * 80 + 40} />
                    </div>
                    <Skeleton className="h-3 w-48" delay={i * 80 + 60} />
                  </div>
                  <Skeleton className="h-5 w-12" delay={i * 80 + 50} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!gamData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Gamification
          </CardTitle>
          <CardDescription>Start earning points and unlocking achievements!</CardDescription>
        </CardHeader>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">Create your first activity to begin your journey</p>
        </CardContent>
      </Card>
    );
  }

  const progressPercent = calculateProgress();
  const pointsInCurrentLevel = gamData.total_points - getPointsForLevel(gamData.current_level);

  return (
    <div className="space-y-6 animate-content-reveal">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Your Progress</h3>
        <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.ACHIEVEMENTS)}>
          <Trophy className="h-4 w-4 mr-2" />
          View All Achievements
        </Button>
      </div>

      {/* Level & Progress Card */}
      <Card className="bg-linear-to-br from-primary/10 to-primary/5">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-6 w-6 text-primary" />
                Level {gamData.current_level}
              </CardTitle>
              <CardDescription className="mt-2">
                {gamData.total_points.toLocaleString()} total points
              </CardDescription>
            </div>
            <TrendingUp className="h-8 w-8 text-primary" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Level Progress</span>
              <span className="font-medium">
                {pointsInCurrentLevel} / {gamData.points_to_next_level} XP
              </span>
            </div>
            <Progress value={progressPercent} className="h-3" />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {(gamData.points_to_next_level - pointsInCurrentLevel).toLocaleString()} XP to level {gamData.current_level + 1}
            </p>
          </div>

          {/* Activity Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-4 border-t">
            <div className="text-center p-3 rounded-lg bg-background/50">
              <p className="text-2xl font-bold">{gamData.sessions_created}</p>
              <p className="text-xs text-muted-foreground">Sessions</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background/50">
              <p className="text-2xl font-bold">{gamData.messages_sent}</p>
              <p className="text-xs text-muted-foreground">Messages</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background/50">
              <p className="text-2xl font-bold">{gamData.memories_created}</p>
              <p className="text-xs text-muted-foreground">Memories</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background/50">
              <p className="text-2xl font-bold">{gamData.projects_created}</p>
              <p className="text-xs text-muted-foreground">Projects</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background/50">
              <p className="text-2xl font-bold">{gamData.boards_created}</p>
              <p className="text-xs text-muted-foreground">Boards</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background/50">
              <p className="text-2xl font-bold">{unlockedAchievements.size}</p>
              <p className="text-xs text-muted-foreground">Achievements</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Achievements Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Achievements ({unlockedAchievements.size} / {achievements.length})
          </CardTitle>
          <CardDescription>Unlock achievements to earn bonus points</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="w-full flex-wrap h-auto">
              <TabsTrigger value="all">All</TabsTrigger>
              {categories.map(cat => (
                <TabsTrigger key={cat} value={cat} className="capitalize">
                  {cat}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="all" className="space-y-3 mt-4">
              {achievements.map((ach) => {
                const isUnlocked = unlockedAchievements.has(ach.id);
                return (
                  <div
                    key={ach.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      isUnlocked 
                        ? 'bg-linear-to-r from-primary/10 to-background' 
                        : 'bg-muted/30 opacity-60'
                    }`}
                  >
                    <div className="text-3xl">{isUnlocked ? ach.icon : '🔒'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium truncate">{ach.name}</p>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${TIER_COLORS[ach.tier as keyof typeof TIER_COLORS]}/20 capitalize`}
                        >
                          {ach.tier}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{ach.description}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-medium text-primary">+{ach.points_reward}</p>
                      {isUnlocked && <Badge variant="secondary" className="text-xs mt-1">✓</Badge>}
                    </div>
                  </div>
                );
              })}
            </TabsContent>

            {categories.map(cat => (
              <TabsContent key={cat} value={cat} className="space-y-3 mt-4">
                {getAchievementsByCategory(cat).map((ach) => {
                  const isUnlocked = unlockedAchievements.has(ach.id);
                  return (
                    <div
                      key={ach.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        isUnlocked 
                          ? 'bg-linear-to-r from-primary/10 to-background' 
                          : 'bg-muted/30 opacity-60'
                      }`}
                    >
                      <div className="text-3xl">{isUnlocked ? ach.icon : '🔒'}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium truncate">{ach.name}</p>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${TIER_COLORS[ach.tier as keyof typeof TIER_COLORS]}/20 capitalize`}
                          >
                            {ach.tier}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{ach.description}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium text-primary">+{ach.points_reward}</p>
                        {isUnlocked && <Badge variant="secondary" className="text-xs mt-1">✓</Badge>}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
